import type { CityConfig } from "../hawaii-city/types";

export const waikikiConfig: CityConfig = {
  city: "Waikiki",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "waikiki",
  route: "/cities/waikiki",
  title: "Emergency & Expedited Passport Services in Waikiki, HI — Honolulu's Tourist Hub | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Waikiki, HI. Fast passport renewal and urgent passport help for Waikiki visitors, Ala Moana, and Diamond Head residents. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Waikiki, HI",
  heroSubtitle: "Waikiki, Ala Moana, and Diamond Head residents and visitors — get your passport fast without leaving Honolulu. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20Waikiki%20Beach%20Honolulu%20Hawaii%20at%20golden%20hour%2C%20iconic%20Diamond%20Head%20crater%20in%20background%2C%20palm%20trees%20along%20the%20shoreline%2C%20turquoise%20Pacific%20Ocean%20with%20surfers%2C%20warm%20sunset%20lighting%2C%20luxury%20hotels%20in%20distance%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=waikiki-hero-01&orientation=landscape",
  heroImageAlt: "Waikiki Beach Honolulu with Diamond Head and turquoise ocean at sunset",
  geoPosition: "21.2793;-157.8292",
  geoPlacename: "Waikiki, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "About 2 miles west in downtown Honolulu",
  },
  nearbyCitiesZips: [
    { name: "Waikiki", zips: "96815" },
    { name: "Honolulu", zips: "96815" },
    { name: "Ala Moana", zips: "96814" },
    { name: "Diamond Head", zips: "96816" },
    { name: "Kahala", zips: "96816" },
    { name: "Kaimuki", zips: "96816" },
    { name: "Manoa", zips: "96822" },
    { name: "Moiliili", zips: "96826" },
    { name: "McCully", zips: "96826" },
    { name: "Kailua", zips: "96734" },
    { name: "Kaneohe", zips: "96744" },
    { name: "Pearl City", zips: "96782" },
    { name: "Aiea", zips: "96701" },
    { name: "Kapolei", zips: "96707" },
    { name: "Mililani", zips: "96789" },
    { name: "Waipahu", zips: "96797" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Waikiki hotel or residence in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Waikiki to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for Waikiki residents and visitors." },
  ],
  testimonials: [
    {
      name: "Alexandra P.",
      location: "Waikiki, HI",
      rating: 5,
      text: "I was staying in Waikiki for a conference and realized my passport was expired the day before my flight to Singapore. Fast Passport Center had my renewal processed in 3 days. I was amazed it could happen that fast from Hawaii.",
      service: "Passport Renewal",
    },
    {
      name: "Marcus W.",
      location: "Ala Moana, HI",
      rating: 5,
      text: "Our honeymoon cruise to the South Pacific was in 2 weeks and my wife's passport was nowhere to be found. They walked us through the replacement process from our Ala Moana apartment and had a new passport delivered with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Yuki T.",
      location: "Kahala, HI",
      rating: 5,
      text: "Emergency trip to Japan for a family matter. I'm in Kahala near Waikiki and FPC handled everything remotely. The passport arrived in 4 days. Their team was incredibly responsive and professional.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "waikiki-usps",
      name: "Waikiki Post Office",
      type: "USPS Passport Acceptance",
      address: "330 Saratoga Rd",
      city: "Honolulu",
      state: "HI",
      zip: "96815",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Waikiki Beach area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-usps-waikiki",
      name: "Honolulu Main Post Office",
      type: "USPS Passport Acceptance",
      address: "3350 Manoa Rd",
      city: "Honolulu",
      state: "HI",
      zip: "96822",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Manoa — about 3 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Honolulu area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "downtown-usps-waikiki",
      name: "Downtown Honolulu Post Office",
      type: "USPS Passport Acceptance",
      address: "3600 Waialae Ave",
      city: "Honolulu",
      state: "HI",
      zip: "96816",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm, Sat 9am–12pm",
      distance: "Kaimuki — about 2 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Downtown Honolulu option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-usps-waikiki",
      name: "Kailua Post Office",
      type: "USPS Passport Acceptance",
      address: "130 Kailua Rd",
      city: "Kailua",
      state: "HI",
      zip: "96734",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Windward Oahu — about 15 miles",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Kailua area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Waikiki, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Waikiki or Honolulu post office can take 2–6 months. For Waikiki residents and visitors, our courier service is the fastest option.",
    },
    {
      q: "Do I need to visit a passport agency in Waikiki?",
      a: "No. Most Waikiki and Honolulu travelers start online and ship documents via overnight courier. The Waikiki, Manoa, or Kaimuki Post Offices can serve as acceptance facilities if needed, but you don't need to visit the downtown Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility to Waikiki?",
      a: "The Waikiki Post Office at 330 Saratoga Rd and the Downtown Honolulu Post Office at 3600 Waialae Ave both accept passport applications by appointment. Honolulu Main Post Office in Manoa is also an option.",
    },
    {
      q: "What if I'm a tourist in Waikiki and need a passport emergency?",
      a: "We frequently help tourists and visitors in Waikiki, Ala Moana, and Kahala who discover passport issues while traveling. We can ship to your hotel address and coordinate return shipping so you don't miss your flight.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Waikiki or Downtown Honolulu Post Office are the most convenient options for Waikiki residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Waikiki work?",
      a: "You can ship overnight using FedEx or UPS from Waikiki. Most Waikiki residents and visitors use the FedEx location near Ala Moana Center or the UPS Store on Kalakaua Avenue.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Waikiki?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Waikiki families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide Waikiki residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Waikiki", address: "2255 Kuhio Ave, Honolulu, HI 96815", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Ala Moana", address: "1488 S King St, Honolulu, HI 96814", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Waikiki", address: "2222 Kuhio Ave, Honolulu, HI 96815", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Waikiki", address: "2255 Kuhio Ave, Honolulu, HI 96815", hours: "Call for hours" },
    { name: "Walmart — Keeaumoku", address: "700 Keeaumoku St, Honolulu, HI 96814", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Honolulu DMV — Downtown Satellite City Hall",
      address: "650 S King St, Honolulu, HI 96813",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kapalama DMV — Dillingham Blvd",
      address: "1188 Dillingham Blvd, Honolulu, HI 96817",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Kalihi area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "About 8 miles west — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii. Most Waikiki travelers fly from HNL for international trips.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Inter-island connection option. Some Waikiki residents connect through Maui for international travel.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Waikiki and Honolulu.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Waikiki travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Waikiki residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Waikiki families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Waikiki residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Waikiki and Honolulu travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Waikiki residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Waikiki residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Waikiki residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Waikiki, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Waikiki residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Waikiki travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Waikiki travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Waikiki." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to visit the downtown agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — downtown Honolulu location", fpc: "Most travelers ship documents overnight from Waikiki", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Pearl City", route: "/cities/pearl-city" },
    { name: "Kaneohe", route: "/cities/kaneohe" },
    { name: "Kapolei", route: "/cities/kapolei" },
    { name: "Mililani", route: "/cities/mililani" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Waikiki home or hotel, ship with our prepaid label from any UPS/FedEx location near Ala Moana Center, or schedule a drop-off. Most Waikiki clients ship overnight.",
  fpcOfficeDistance: "Serving Waikiki and all of Honolulu remotely",
  serviceAreaIntro: "Whether you are in Waikiki, Ala Moana, Diamond Head, or anywhere in Honolulu, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.",
  nearbyCitiesIntro: "Anywhere in Honolulu — start online, ship overnight from Waikiki, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Waikiki as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Waikiki travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Waikiki, Ala Moana, and Kahala who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Waikiki and Honolulu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Diamond Head Crater",
      description: "The iconic symbol of Honolulu, Diamond Head is visible from nearly every point in Waikiki. The 1.4-mile hike to the summit offers breathtaking 360° views of Oahu — a fitting symbol of Hawaii's connection to the wider world.",
      imageUrl: "https://readdy.ai/api/search-image?query=Diamond%20Head%20crater%20Waikiki%20Honolulu%20Hawaii%2C%20iconic%20volcanic%20cone%20viewed%20from%20Waikiki%20beach%2C%20lush%20green%20crater%20slopes%2C%20turquoise%20Pacific%20Ocean%20in%20foreground%2C%20warm%20sunset%20lighting%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=waikiki-diamond-head&orientation=landscape",
    },
    {
      name: "Waikiki Beach",
      description: "The most famous beach in the Pacific. Waikiki's golden sands and gentle waves welcome millions of international visitors annually — many of whom discover urgent passport needs right here.",
      imageUrl: "https://readdy.ai/api/search-image?query=Waikiki%20Beach%20golden%20hour%20Hawaii%2C%20iconic%20shoreline%20with%20surfers%20and%20swimmers%2C%20Diamond%20Head%20in%20distance%2C%20luxury%20hotels%20lining%20the%20beach%2C%20warm%20sunset%20colors%2C%20turquoise%20calm%20ocean%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=waikiki-beach&orientation=landscape",
    },
    {
      name: "Ala Moana Center",
      description: "The largest open-air shopping center in the world, Ala Moana is 5 minutes from Waikiki and home to FedEx Office, passport photo services, and everything needed to get your documents shipped fast.",
      imageUrl: "https://readdy.ai/api/search-image?query=Ala%20Moana%20Center%20Hawaii%2C%20large%20outdoor%20shopping%20mall%20with%20tropical%20landscaping%2C%20palm%20trees%2C%20open%20air%20walkways%2C%20busy%20retail%20area%20with%20warm%20tropical%20lighting%2C%20professional%20architectural%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=waikiki-alamoana&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-hotel-line", title: "Tourists & Visitors", description: "Waikiki hosts millions of visitors annually. If you're staying in a hotel and discovered a passport issue, we can ship to your hotel address and return via overnight — keeping you on schedule." },
    { icon: "ri-flight-takeoff-line", title: "HNL Is 8 Miles West", description: "Honolulu International Airport is about 20 minutes from Waikiki. Many travelers realize their passport expires just days before departure — urgent processing is our specialty." },
    { icon: "ri-ship-line", title: "Cruise Ship Passengers", description: "Aloha Tower Marketplace in nearby downtown serves as Honolulu's cruise terminal. Cruise passengers need valid passports for every international port of call — check yours now." },
    { icon: "ri-truck-line", title: "Ship from Waikiki", description: "The UPS Store on Kalakaua Ave and FedEx Office at Ala Moana offer overnight shipping. Waikiki residents and hotel guests both use these locations for urgent document shipping." },
    { icon: "ri-global-line", title: "Top International Destination", description: "Japan, Australia, and Korea are the most common international destinations for Honolulu travelers. Check passport validity 6+ months ahead of planned departure." },
    { icon: "ri-calendar-event-line", title: "Peak Season Warnings", description: "Waikiki is busiest during winter (December–February), spring break, and summer. Government passport processing backs up significantly during these periods — use a courier service." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
};